import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import Loader from "../../common/Loader";
import Button from "../../common/Button";
import FormInput from "../../common/FormInput";
import _ from "../../../i18n";
import {
  saveGoPeopleContact,
  updateGoPeopleContact,
  verifyGoPeopleContactByEmail,
} from "../../../api";
import {
  GENDER_OPTIONS,
  LANGUAGES,
  SELECT_STYLES,
} from "../../../config";
import { validateEmail, validateUen, parseApiError } from "../../../utils";
import styles from "./Contact.module.scss";

// Same pattern as the collaborator and client forms, itself copied from the
// gopeople name validation tag, so the three forms accept exactly what the
// backend accepts.
const NAME_REGEX = /^[\p{L}\s'-]{2,50}$/u;

// Only these three are required. The phone, the company number and the company
// name are optional, yet every empty field used to be reported as mandatory.
const REQUIRED_FIELDS = ["email", "lastname", "firstname"];

const ContactForm = (props) => {
  const auth = useSelector((state) => state.auth);
  const [email, setEmail] = useState("");
  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState(GENDER_OPTIONS[0]);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [uen, setUen] = useState("");
  const [uName, setUname] = useState("");
  const [fonction, setFonction] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);

  const [disableFields, setDisableFields] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [fetchedUser, setFetchedUser] = useState(null);
  const [fetchedContact, setFetchedContact] = useState(null);
  const { goPeopleCommunityId } = auth;

  const [errors, setErrors] = useState({
    email: "",
    lastname: "",
    firstname: "",
    phone: "",
    uen: "",
    name: "",
  });

  useEffect(() => {
    if (props.emailValue) {
      setEmail(props.emailValue);
      handleEmailProcess(props.emailValue);
    }
  }, [props.emailValue]);

  useEffect(() => {
    if (props.currentItem) {
      if (props.currentItem.mainEmail) {
        setEmail(props.currentItem.mainEmail);
      }
      setFirstname(
        props.currentItem.firstName ? props.currentItem.firstName : ""
      );
      setLastname(props.currentItem.lastName ? props.currentItem.lastName : "");
      if (props.currentItem.language)
        setLanguage(
          LANGUAGES.filter((e) => e.value === props.currentItem.language)[0]
        );
      setPhone(props.currentItem.mainPhone ? props.currentItem.mainPhone : "");
      if (props.currentItem.gender) {
        const g =
          props.currentItem.gender === "M"
            ? "MALE"
            : props.currentItem.gender === "F"
              ? "FEMALE"
              : props.currentItem.gender;
        const gend = GENDER_OPTIONS.filter((e) => e.value === g);
        if (gend?.length > 0) {
          setGender(gend[0]);
        }
      }
      // The company and the function are read from the contact itself. They used to
      // be read from contactClient.details, a shape the API never returns, so both
      // columns and both fields were always empty.
      if (props.currentItem.company?.uen) {
        setUen(props.currentItem.company.uen);
      }
      if (props.currentItem.company?.name) {
        setUname(props.currentItem.company.name);
      }
      if (props.currentItem.function) {
        setFonction(props.currentItem.function);
      }
      setIsValidForm(true);
    }
  }, [props.currentItem]);
  useEffect(() => {
    if (email.length > 0 && firstname.length > 0 && lastname.length > 0) {
      setIsValidForm(
        errors.email || errors.lastname || errors.firstname || errors.phone
          ? false
          : true
      );
    }
  }, [errors]);

  // Pure so that validate() can decide on freshly computed values instead of
  // reading the errors state, which is one render behind.
  const getFieldError = (fieldName, fieldValue) => {
    const value = fieldValue ?? "";
    if (value.length === 0) {
      return REQUIRED_FIELDS.includes(fieldName) ? _("required_field") : "";
    }
    switch (fieldName) {
      case "email":
        return validateEmail(value) ? "" : _("validate_email");
      case "lastname":
      case "firstname":
        // A bare minimum length let digits and symbols through, and said nothing
        // about why. Trimmed because the backend trims the name before validating.
        return NAME_REGEX.test(value.trim()) ? "" : _("validate_name");
      default:
        return "";
    }
  };

  // The phone field is a PhoneInput, so its value is E.164 and libphonenumber is
  // the right validator here, not the character pattern the two other forms use
  // on their plain text inputs.
  const getPhoneError = (fieldValue) => {
    if (!fieldValue) {
      return "";
    }
    return isValidPhoneNumber(fieldValue) ? "" : _("validate_phone");
  };

  const handleKeyUp = (fieldName, fieldValue) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: getFieldError(fieldName, fieldValue),
    }));
  };

  // Validation errors coming back from the API are attached to their field, and
  // the toast is only used for what could not be tied to one. The canonical field
  // names returned by parseApiError already match this form's error keys.
  const showApiError = (e) => {
    const { fieldErrors, message } = parseApiError(e);

    if (Object.keys(fieldErrors).length > 0) {
      setErrors((prevErrors) => ({ ...prevErrors, ...fieldErrors }));
    }

    toast.error(message);
  };

  // Maps the fields returned by the verify endpoint onto the form. The payload
  // may use snake_case (contact record) or camelCase (user record), and phones
  // may arrive as an array or a single string, so we read both shapes.
  const fillFormFromMatch = (d) => {
    setFirstname(d.first_name ?? d.firstname ?? "");
    setLastname(d.last_name ?? d.lastname ?? "");

    // Contact core uses "language"; user core uses "main_language".
    const lang = d.language ?? d.main_language;
    if (lang) {
      const found = LANGUAGES.filter((e) => e.value === lang);
      if (found.length) {
        setLanguage(found[0]);
      }
    }

    // Contact uses "genre"; user uses "sex" (both M/F).
    const rawGender = d.genre ?? d.gender ?? d.sex;
    if (rawGender) {
      const g =
        rawGender === "M" ? "MALE" : rawGender === "F" ? "FEMALE" : rawGender;
      const gend = GENDER_OPTIONS.filter((e) => e.value === g);
      if (gend?.length > 0) {
        setGender(gend[0]);
      }
    }

    const phones = Array.isArray(d.phones)
      ? d.phones
      : d.phone
        ? [d.phone]
        : [];
    if (phones.length) {
      setPhone(phones[0]);
    }
  };

  // The verify endpoint checks the community contacts first, then falls back to
  // a global user. A community contact is serialized with community_id (a core
  // field); a global user is not (it carries selected_community_id instead), so
  // its absence tells the two apart. Extra markers stay as defensive fallbacks.
  const isCommunityContactMatch = (d) => {
    if (d.community_id) return true;
    if (typeof d.is_contact === "boolean") return d.is_contact;
    return Boolean(d.contact_type || d.contact_id);
  };

  const handleEmailProcess = (email) => {
    setIsFetchingUser(true);
    // Drop the previous match before looking the new address up. Neither branch
    // below clears the other state, so checking a second email used to keep the
    // first one's contact and save against it.
    setFetchedContact(null);
    setFetchedUser(null);
    verifyGoPeopleContactByEmail(goPeopleCommunityId, email)
      .then((match) => {
        // 404 -> no existing contact/user: keep the form empty and editable.
        if (!match) {
          setDisableFields(false);
          setFirstname("");
          setLastname("");
          return;
        }

        fillFormFromMatch(match);
        setDisableFields(true);
        setErrors({
          email: "",
          lastname: "",
          firstname: "",
          phone: "",
        });

        // An existing community contact is reused as-is on save (its uuid is the
        // contact id). A global user is turned into a new contact linked via
        // user_id. Both shapes expose their id as "uuid".
        if (isCommunityContactMatch(match)) {
          setFetchedContact({ ...match, uuid: match.uuid ?? match.id });
        } else {
          setFetchedUser({ ...match, uuid: match.uuid ?? match.id });
        }
      })
      .catch((e) => {
        setDisableFields(false);
        handleResetForm(false);
      })
      .finally(() => {
        setIsFetchingUser(false);
      });
  };

  const handleEmailBlur = (e) => {
    if (email.length > 0 && validateEmail(email)) {
      handleEmailProcess(email);
    }
  };

  // The company number is optional, and a malformed one is blocked here rather
  // than by the backend: validateUen requires the ten Belgian digits, which is
  // stricter than what the contact company accepts, so anything passing this can
  // no longer be rejected on the server.
  const getUenError = (fieldValue) => {
    if (!fieldValue) {
      return "";
    }
    return validateUen(fieldValue) ? "" : _("validate_uen");
  };

  const validate = () => {
    // "let errors = []" used to shadow the errors state here, so every lookup below
    // returned undefined and this function always answered "no error": nothing was
    // ever blocked at submit time. The phone is included now that its message is
    // visible, and it stays optional.
    const newErrors = {
      email: getFieldError("email", email),
      lastname: getFieldError("lastname", lastname),
      firstname: getFieldError("firstname", firstname),
      phone: getPhoneError(phone),
      uen: getUenError(uen),
    };
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));

    return Object.values(newErrors).some(Boolean);
  };

  // The company is stored on the contact itself, so the form no longer creates a
  // client folder on the side to carry it. Empty fields are left out rather than
  // sent blank, so saving a form where the user typed nothing about the company
  // cannot clear what a contact already has.
  const buildCompanyFields = () => {
    const fields = {};
    const name = uName.trim();
    // Same normalization the folder payload used: the backend company rejects
    // dots and spaces.
    const normalizedUen = uen.replace(/\.|\s|BE|be/g, "");

    if (name || normalizedUen) {
      fields.company = { name, uen: normalizedUen };
    }
    if (fonction.trim()) {
      fields.function = fonction.trim();
    }

    return fields;
  };

  const save = () => {
    handleSave();
  };

  const afterSave = () => {
    setIsSaving(false);
    props.afterSave();
    toast.success(
      props.currentItem ? _("successfully_updated") : _("successfully_added")
    );
    handleResetForm();
    if (props.setSideBar) {
      props.setSideBar("DEFAULT");
    }
  };

  const failSave = (e) => {
    setIsSaving(false);
    if (e.status === 409) {
      toast.warning(_("contact_already_exist"));
    } else {
      // Was either the backend sentence in English, or a bare "Erreur".
      // parseApiError reads the three shapes the API answers with and attaches
      // what it can to the field at fault.
      showApiError(e);
    }
  };

  const handleSave = async () => {
    // validate() runs before the saving flag is raised. The flag used to be set
    // first and was never cleared on this early return, so a blocked submit left
    // the button spinning for good. Unreachable until now, since validate() always
    // answered that everything was fine.
    if (validate()) {
      return null;
    }
    setIsSaving(true);
    if (!isFetchingUser && !isSaving) {
      const companyFields = buildCompanyFields();

      // An email already known as a contact of this community is updated in place.
      // Only the company and the function can be edited here, the identity fields
      // are locked as soon as the lookup matches.
      if (fetchedContact) {
        if (Object.keys(companyFields).length === 0) {
          afterSave();
          return;
        }
        updateGoPeopleContact(
          goPeopleCommunityId,
          fetchedContact.uuid,
          companyFields
        )
          .then(() => afterSave())
          .catch(failSave);
        return;
      }

      saveGoPeopleContact(goPeopleCommunityId, {
        emails: [email],
        first_name: firstname,
        last_name: lastname,
        community_id: goPeopleCommunityId,
        phones: phone ? [phone] : [],
        language: language.value,
        genre: gender.value,
        user_id: fetchedUser ? fetchedUser.uuid : null,
        ...companyFields,
      })
        .then(() => afterSave())
        .catch(failSave);
    }
  };

  const handleResetForm = (resetEmail = true) => {
    if (resetEmail) {
      setEmail("");
    }
    setFirstname("");
    setLastname("");
    setPhone("");
    setDisableFields(false);
    setGender(GENDER_OPTIONS[0]);
    setLanguage(LANGUAGES[0]);
    setErrors({
      email: "",
      lastname: "",
      firstname: "",
      phone: "",
      uen: "",
      name: "",
    });
    setUen("");
    setUname("");
    setFonction("");
    props.setCurrentItem(null);
    document
      .getElementById("contactForm")
      .scroll({ top: 0, behavior: "smooth" });
    setFetchedUser(null);
    // fetchedContact was never cleared here. It now decides whether the save
    // updates an existing contact, so a leftover match would write the company of
    // the next person onto the previous one.
    setFetchedContact(null);
  };
  return (
    <div className={props.addFormClass ? styles.form : ""}>
      <div
        id={"contactForm"}
        className={`${styles.form_content} ${
          props.addFormClass && styles.form_content_pad
        } ${props.addFormClass ? styles.form_content_settings : ""} ${
          isFetchingUser && styles.loading
        }`}
      >
        {props.addFormClass && (
          <div className={styles.titles}>
            <h4>Ajout Manuel d'un contact</h4>
            <p>Remplissez les champs pour ajouter un contact</p>
          </div>
        )}
        <FormInput
          name="email"
          required={true}
          label={_("email_address")}
          autocomplete="off"
          error={errors.email}
          value={email}
          onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
          handleBlur={(e) => handleEmailBlur(e)}
          onChange={(e) => setEmail(e.target.value)}
        />

        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-6">
            <FormInput
              name="lastname"
              required={true}
              label={_("lastname")}
              autocomplete="off"
              error={errors.lastname}
              value={lastname}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => setLastname(e.target.value)}
              disabled={disableFields}
            />
          </div>
          <div className="cell small-12 medium-6">
            <FormInput
              name="firstname"
              required={true}
              label={_("firstname")}
              autocomplete="off"
              error={errors.firstname}
              value={firstname}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => setFirstname(e.target.value)}
              disabled={disableFields}
            />
          </div>
        </div>
        <div className={styles.phoneGroup}>
          <PhoneInput
            defaultCountry="BE"
            placeholder="Enter phone number"
            value={phone}
            disabled={disableFields}
            onChange={(val) => {
              setPhone(val);
              setErrors((prevErrors) => ({
                ...prevErrors,
                phone: getPhoneError(val),
              }));
            }}
          />
          {/* This message was commented out, so an invalid number disabled the
              submit button with nothing on screen to explain why. */}
          <span className={styles.errorSpan}>{errors.phone}</span>
        </div>

        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-6">
            <div className="ttp-form-group">
              <label className="ttp-label">{_("gender_1")}</label>
              <Select
                styles={SELECT_STYLES}
                options={GENDER_OPTIONS}
                isSearchable={false}
                value={gender}
                onChange={(e) => setGender(e)}
                isDisabled={disableFields}
              />
            </div>
          </div>
          <div className="cell small-12 medium-6">
            <div className="ttp-form-group">
              <label className="ttp-label">{_("language")}</label>
              <Select
                styles={SELECT_STYLES}
                options={LANGUAGES}
                isSearchable={false}
                value={language}
                onChange={(e) => setLanguage(e)}
                isDisabled={disableFields}
              />
            </div>
          </div>
        </div>

        <FormInput
          name="uen"
          placeholder="BE XXXX.XXX.XXX"
          label={_("business_number")}
          value={uen}
          autoComplete="none"
          error={errors.uen}
          onChange={(e) => {
            setErrors((prevErrors) => ({
              ...prevErrors,
              uen: getUenError(e.target.value),
            }));
            setUen(e.target.value);
          }}
        ></FormInput>

        <FormInput
          name="name"
          value={uName}
          label={_("company_name")}
          autoComplete="none"
          error={errors.name}
          onChange={(e) => {
            setUname(e.target.value);
            if (errors.name) setErrors({ ...errors, name: "" });
          }}
        ></FormInput>

        <FormInput
          name="fonction"
          label={_("Fonction")}
          value={fonction}
          autoComplete="none"
          onChange={(e) => setFonction(e.target.value)}
        ></FormInput>
      </div>
      <div className={styles.actions}>
        <Button
          variant="default"
          onClick={() => {
            handleResetForm();
            if (props.setSideBar) {
              props.setSideBar("DEFAULT");
            }
          }}
        >
          {_("cancel")}
        </Button>
        {isSaving ? (
          <Button
            variant="primary"
            style={{ paddingTop: "15px", paddingBottom: "15px" }}
          >
            <Loader
              style={{
                height: "10px",
              }}
              color={"#fff"}
            />
          </Button>
        ) : (
          <Button disabled={!isValidForm} onClick={() => save()}>
            {props.currentItem ? _("edit") : _("add")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
